import { GoogleMap } from "@react-google-maps/api";
import { Alert, Snackbar } from "@mui/material";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addNewGeofence,
  deleteGeofence,
  updateGeofence,
} from "../../api/new-api";
import { ErrorAlert } from "../../components/alerts";
import { ConfirmDialog } from "../../components/confirm-dialog";
import { PEProgressBar } from "../../components/linear-progress";
import {
  AUSTRALIA_VIEW,
  collectFenceCoordinates,
  fitMapToCoordinates,
  hasUsableCoordinates,
} from "../../utils/map-view";
import DraftPolygon from "./DraftPolygon";
import FenceLabel from "./FenceLabel";
import FenceListPanel from "./FenceListPanel";
import FenceMarkers from "./FenceMarkers";
import InvalidFencesPanel from "./InvalidFencesPanel";
import MapSearchBox from "./MapSearchBox";
import PolygonContainer from "./PolygonContainer";
import SelectedFencePanel from "./SelectedFencePanel";
import { validateFence } from "./fence-validation";
import {
  computeAreaM2,
  createFence,
  createSquareInView,
  expandBounds,
  fenceIntersects,
  fetchFences,
  normaliseCoordinates,
} from "./mapUtils";
import { EditableFence, LatLng } from "./types";
import { useUnsavedChangesWarning } from "./useUnsavedChangesWarning";

const mapContainerStyle = { height: "70vh", minHeight: "500px", width: "100%" };

/**
 * Below this zoom fences are drawn as clustered markers instead of polygons.
 * A venue fence is building-sized, so under street zoom its polygon is a dot
 * anyway and hundreds of them only cost render time.
 */
const POLYGON_MIN_ZOOM = 11;

/** How far beyond the visible map to keep polygons mounted, as a fraction of the view. */
const VIEWPORT_MARGIN = 0.5;

type Viewport = { zoom: number; bounds: google.maps.LatLngBounds };

const mapStyles: google.maps.MapTypeStyle[] = [
  { featureType: "poi", elementType: "all", stylers: [{ visibility: "off" }] },
];

const replaceFence = (list: EditableFence[], fence: EditableFence) =>
  list.map((f) => (f.locationId === fence.locationId ? fence : f));

const withoutFence = (list: EditableFence[], locationId: string) =>
  list.filter((f) => f.locationId !== locationId);

const describeError = (err: unknown, fallback: string) => {
  const message = err instanceof Error ? err.message : "";
  return message ? `${fallback} (${message})` : fallback;
};

const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.tagName === "INPUT" || target.tagName === "TEXTAREA");

/**
 * Geofence editor. `fences` is the single working copy shown on the map;
 * `savedFences` mirrors the server so Undo can restore a fence. Everything else
 * (selection, unsaved list, invalid list) is derived from those two.
 */
const NewFenceConfigContainer = () => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [fences, setFences] = useState<EditableFence[]>([]);
  const [savedFences, setSavedFences] = useState<EditableFence[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Draw mode: null when off, otherwise the corners placed so far.
  const [draftPoints, setDraftPoints] = useState<LatLng[] | null>(null);
  const isDrawing = draftPoints !== null;
  const [pendingDelete, setPendingDelete] = useState<EditableFence | null>(null);
  const [viewport, setViewport] = useState<Viewport | null>(null);
  const hasFramedMapRef = useRef(false);

  // Fences without a real position can't be drawn; they get their own panel.
  const { drawableFences, invalidFences } = useMemo(() => {
    const drawable: EditableFence[] = [];
    const invalid: EditableFence[] = [];
    fences.forEach((f) =>
      (f.status !== "ACTIVE" || hasUsableCoordinates(f) ? drawable : invalid).push(f)
    );
    return { drawableFences: drawable, invalidFences: invalid };
  }, [fences]);

  const selectedFence = useMemo(
    () => fences.find((f) => f.locationId === selectedId) ?? null,
    [fences, selectedId]
  );

  const labelledFence = useMemo(() => {
    const id = hoveredId ?? selectedId;
    return id ? drawableFences.find((f) => f.locationId === id) ?? null : null;
  }, [drawableFences, hoveredId, selectedId]);

  const unsavedCount = useMemo(
    () => fences.filter((f) => f.status !== "ACTIVE").length,
    [fences]
  );

  // The list renders from `drawableFences` straight away; the map layer takes
  // the deferred copy so React can paint the list before mounting map shapes.
  const mapFences = useDeferredValue(drawableFences);

  const showPolygons = viewport !== null && viewport.zoom >= POLYGON_MIN_ZOOM;

  // Which fences get a real polygon. Unsaved and selected fences always do so
  // the user can see and edit their work; saved fences only when zoomed in and
  // near the current view.
  const { polygonFences, markerFences } = useMemo(() => {
    const polygons: EditableFence[] = [];
    const markers: EditableFence[] = [];
    const area =
      showPolygons && viewport ? expandBounds(viewport.bounds, VIEWPORT_MARGIN) : null;

    mapFences.forEach((fence) => {
      const mustDraw = fence.status !== "ACTIVE" || fence.locationId === selectedId;
      if (mustDraw || (area && fenceIntersects(fence, area))) {
        polygons.push(fence);
      } else if (!showPolygons) {
        markers.push(fence);
      }
    });

    return { polygonFences: polygons, markerFences: markers };
  }, [mapFences, selectedId, showPolygons, viewport]);

  useUnsavedChangesWarning(unsavedCount, "geofence change");

  // Load from the server. Local unsaved edits survive a reload so that saving
  // one fence never throws away work in progress on another.
  const loadFences = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const remote = await fetchFences();
      setSavedFences(remote);
      setFences((current) => {
        const unsaved = current.filter((f) => f.status !== "ACTIVE");
        const unsavedIds = new Set(unsaved.map((f) => f.locationId));
        return [
          ...remote.filter((f) => !unsavedIds.has(f.locationId)),
          ...unsaved,
        ];
      });
    } catch (err) {
      console.error("Error fetching geofences", err);
      setError(describeError(err, "Could not load geofences."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFences();
  }, [loadFences]);

  // Frame the map around the saved fences, but only once. This page is an
  // editor: refitting after every save would yank the map away mid-edit, so
  // later fetches deliberately leave the user's view alone. With no fences at
  // all the map stays on the country view it mounted with.
  useEffect(() => {
    if (!map || hasFramedMapRef.current || savedFences.length === 0) return;

    hasFramedMapRef.current = true;
    fitMapToCoordinates(map, collectFenceCoordinates(savedFences));
  }, [map, savedFences]);

  // Escape: leave draw mode first, otherwise clear the selection.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (isDrawing) {
        setDraftPoints(null);
      } else if (selectedId) {
        if (isTypingTarget(event.target)) (event.target as HTMLElement).blur();
        setSelectedId(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isDrawing, selectedId]);

  const addFence = (fence: EditableFence) => {
    setFences((current) => [...current, fence]);
    setSelectedId(fence.locationId);
    setError(null);
  };

  const handleIdle = () => {
    const zoom = map?.getZoom();
    const bounds = map?.getBounds();
    if (zoom === undefined || !bounds) return;
    setViewport({ zoom, bounds });
  };

  const handleSelectOnMap = useCallback(
    (locationId: string) =>
      setSelectedId((current) => (current === locationId ? null : locationId)),
    []
  );

  const handleSelectAndZoom = useCallback(
    (fence: EditableFence) => {
      setSelectedId(fence.locationId);
      if (map && hasUsableCoordinates(fence)) {
        fitMapToCoordinates(map, fence.coordinates);
      }
    },
    [map]
  );

  const handleShapeChange = useCallback((fence: EditableFence) => {
    setFences((current) => replaceFence(current, fence));
  }, []);

  const startDrawing = () => {
    setSelectedId(null);
    setError(null);
    setDraftPoints([]);
  };

  const handleMapClick = (event: google.maps.MapMouseEvent) => {
    if (!isDrawing) {
      setSelectedId(null);
      return;
    }
    const point = event.latLng;
    if (!point) return;
    setDraftPoints((current) => [
      ...(current ?? []),
      { lat: point.lat(), lng: point.lng() },
    ]);
  };

  const undoDraftPoint = () =>
    setDraftPoints((current) => (current ? current.slice(0, -1) : current));

  const finishDrawing = () => {
    if (!draftPoints) return;
    if (draftPoints.length < 3) {
      setError("A fence needs at least three corners. Keep clicking on the map.");
      return;
    }
    setDraftPoints(null);
    addFence(createFence(draftPoints, fences.length));
  };

  const handleAddSquare = () => {
    if (!map) return;
    const fence = createSquareInView(map, fences.length);
    if (!fence) {
      setError("The map is still loading. Try again in a moment.");
      return;
    }
    addFence(fence);
  };

  const handleNameChange = (name: string) => {
    if (!selectedFence) return;
    handleShapeChange({
      ...selectedFence,
      name,
      status: selectedFence.status === "NEW" ? "NEW" : "EDITED",
    });
  };

  const handleSave = async (locationId: string) => {
    const fence = fences.find((f) => f.locationId === locationId);
    if (!fence) return;

    const problem = validateFence(fence, fences, computeAreaM2(fence.coordinates));
    if (problem) {
      setSelectedId(locationId);
      setError(problem);
      return;
    }

    setSavingId(locationId);
    setError(null);
    const name = fence.name.trim();
    const coordinates = normaliseCoordinates(fence.coordinates);

    try {
      if (fence.status === "NEW") {
        // The server assigns the real locationId, so drop the temporary fence
        // and reload the list to pick up the saved one.
        await addNewGeofence({ name, coordinates });
        setFences((current) => withoutFence(current, locationId));
        setSelectedId((current) => (current === locationId ? null : current));
        await loadFences();
      } else {
        await updateGeofence(locationId, name, coordinates);
        const saved: EditableFence = { ...fence, name, status: "ACTIVE" };
        setFences((current) => replaceFence(current, saved));
        setSavedFences((current) => replaceFence(current, saved));
        setSelectedId((current) => (current === locationId ? null : current));
      }
      setSuccess(`Saved "${name}".`);
    } catch (err) {
      console.error("Error saving geofence", err);
      setError(describeError(err, `Could not save "${name}".`));
    } finally {
      setSavingId(null);
    }
  };

  const handleUndo = (locationId: string) => {
    const saved = savedFences.find((f) => f.locationId === locationId);
    setFences((current) =>
      saved
        ? replaceFence(current, saved)
        : withoutFence(current, locationId) // NEW fences have nothing to revert to
    );
    setSelectedId((current) => (current === locationId ? null : current));
  };

  const handleConfirmDelete = async () => {
    const fence = pendingDelete;
    if (!fence) return;
    const label = fence.name || "Unnamed fence";

    setSavingId(fence.locationId);
    setError(null);

    try {
      await deleteGeofence(fence.locationId);
      setFences((current) => withoutFence(current, fence.locationId));
      setSavedFences((current) => withoutFence(current, fence.locationId));
      setSelectedId((current) => (current === fence.locationId ? null : current));
      setSuccess(`Deleted "${label}".`);
    } catch (err) {
      console.error("Error deleting geofence", err);
      setError(describeError(err, `Could not delete "${label}".`));
    } finally {
      setSavingId(null);
      setPendingDelete(null);
    }
  };

  return (
    <div className="App">
      {error && <ErrorAlert message={error} />}
      {loading && (
        <div className="my-4">
          <PEProgressBar />
        </div>
      )}

      <div className="flex flex-col gap-4 lg:flex-row">
        {/* Map column */}
        <div className="min-w-0 flex-1 rounded-lg border-2 border-gray-200 bg-slate-100 p-4 shadow">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {isDrawing ? (
              <>
                <span className="min-w-0 flex-1 text-sm font-bold text-blue-800">
                  Click the map to place each corner
                  {draftPoints.length > 0 && ` (${draftPoints.length} placed)`}.
                  Click the first corner or Finish when done.
                </span>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={finishDrawing}
                    disabled={draftPoints.length < 3}
                    className="rounded bg-blue-500 px-3 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Finish
                  </button>
                  <button
                    type="button"
                    onClick={undoDraftPoint}
                    disabled={draftPoints.length === 0}
                    className="rounded bg-gray-500 px-3 py-2 text-sm font-bold text-white hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Undo corner
                  </button>
                  <button
                    type="button"
                    onClick={() => setDraftPoints(null)}
                    className="rounded border border-gray-500 bg-white px-3 py-2 text-sm font-bold text-gray-700 hover:bg-gray-100"
                  >
                    Cancel (Esc)
                  </button>
                </div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={startDrawing}
                  disabled={!map}
                  className="rounded bg-blue-500 px-4 py-2 font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Draw New Geofence
                </button>
                <button
                  type="button"
                  onClick={handleAddSquare}
                  disabled={!map}
                  title="Drops a square in the middle of the current view, sized to fit it"
                  className="rounded border border-blue-500 bg-white px-4 py-2 font-bold text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Add Square Here
                </button>
                <span className="text-xs text-gray-600">
                  {showPolygons
                    ? "Click a fence to select it, then drag its corners or the whole shape."
                    : "Zoom in to see fence shapes. Click a cluster to zoom, or a dot to select."}
                </span>
              </>
            )}
          </div>

          {map && <MapSearchBox map={map} />}

          <GoogleMap
            onLoad={setMap}
            onUnmount={() => setMap(null)}
            onIdle={handleIdle}
            mapContainerStyle={mapContainerStyle}
            center={AUSTRALIA_VIEW.center}
            zoom={AUSTRALIA_VIEW.zoom}
            options={{
              styles: mapStyles,
              disableDoubleClickZoom: true,
              draggableCursor: isDrawing ? "crosshair" : undefined,
            }}
            onClick={handleMapClick}
          >
            {polygonFences.map((fence) => (
              <PolygonContainer
                key={fence.locationId}
                fence={fence}
                isSelected={fence.locationId === selectedId}
                onSelect={handleSelectOnMap}
                onHover={setHoveredId}
                onShapeChange={handleShapeChange}
              />
            ))}

            {markerFences.length > 0 && (
              <FenceMarkers
                fences={markerFences}
                onSelect={handleSelectAndZoom}
                onHover={setHoveredId}
              />
            )}

            {labelledFence && <FenceLabel fence={labelledFence} />}

            {draftPoints && (
              <DraftPolygon points={draftPoints} onClose={finishDrawing} />
            )}
          </GoogleMap>
        </div>

        {/* Side column */}
        <aside className="flex w-full flex-col gap-4 lg:w-96">
          {selectedFence && (
            <SelectedFencePanel
              fence={selectedFence}
              isBusy={savingId === selectedFence.locationId}
              onNameChange={handleNameChange}
              onSave={handleSave}
              onUndo={handleUndo}
              onDelete={setPendingDelete}
              onDeselect={() => setSelectedId(null)}
            />
          )}
          <div className="min-h-[300px] flex-1 lg:max-h-[70vh]">
            <FenceListPanel
              fences={drawableFences}
              selectedId={selectedId}
              unsavedCount={unsavedCount}
              onSelect={handleSelectAndZoom}
            />
          </div>
          <InvalidFencesPanel
            fences={invalidFences}
            savingId={savingId}
            onDelete={setPendingDelete}
          />
        </aside>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete geofence?"
        message={`"${pendingDelete?.name || "Unnamed fence"}" will be removed permanently, including from any venue it is assigned to.\n\nThis cannot be undone.`}
        confirmLabel="Delete"
        destructive
        busy={pendingDelete !== null && savingId === pendingDelete.locationId}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <Snackbar
        open={success !== null}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default NewFenceConfigContainer;
